import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Monitor, Smartphone, LoaderCircle, LogOut, ShieldCheck } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const deviceLabel = (ua = "") => {
  const s = ua.toLowerCase();
  if (/mobile|android|iphone|ipad/.test(s)) return "Móvil";
  if (/electron|emauran/.test(s)) return "App de escritorio";
  if (/windows/.test(s)) return "Windows";
  if (/mac os|macintosh/.test(s)) return "macOS";
  if (/linux/.test(s)) return "Linux";
  return "Navegador";
};

export default function AccountView() {
  const { user, company } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (jti) => {
    setBusy(jti);
    try {
      await api.post(`/auth/sessions/${jti}/revoke`);
      toast.success("Sesión cerrada");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async () => {
    setBusy("others");
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(data.revoked ? `${data.revoked} sesión(es) cerradas` : "No había otras sesiones");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const others = sessions.filter((s) => !s.current).length;

  return (
    <div className="p-4 sm:p-6 space-y-6 overflow-y-auto" data-testid="account-view">
      <div>
        <h1 className="font-head font-bold text-2xl tracking-tight">Mi cuenta</h1>
        <p className="text-muted-foreground text-sm mt-1">Datos de tu perfil y sesiones activas.</p>
      </div>

      <div className="border border-border rounded-md p-4 max-w-lg">
        <div className="grid grid-cols-3 gap-2 text-sm">
          <span className="text-muted-foreground">Nombre</span>
          <span className="col-span-2 font-medium" data-testid="account-name">{user?.name || "—"}</span>
          <span className="text-muted-foreground">Correo</span>
          <span className="col-span-2 font-medium" data-testid="account-email">{user?.email}</span>
          <span className="text-muted-foreground">Empresa</span>
          <span className="col-span-2 font-medium">{company?.name || "—"}</span>
          <span className="text-muted-foreground">Rol</span>
          <span className="col-span-2 font-medium uppercase text-primary text-xs tracking-wider">{user?.role}</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="font-head font-semibold text-lg">Sesiones activas</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            data-testid="revoke-others-btn"
            onClick={revokeOthers}
            disabled={busy === "others" || others === 0}
          >
            {busy === "others" ? <LoaderCircle className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
            Cerrar otras sesiones
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-8">
            <LoaderCircle className="h-4 w-4 animate-spin" /> Cargando sesiones…
          </div>
        ) : (
          <div className="space-y-2 max-w-2xl" data-testid="sessions-list">
            {sessions.map((s) => {
              const isMobile = deviceLabel(s.user_agent) === "Móvil";
              const Icon = isMobile ? Smartphone : Monitor;
              return (
                <div key={s.jti} data-testid={`session-${s.jti}`} className="flex items-center gap-3 border border-border rounded-md px-4 py-3">
                  <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {deviceLabel(s.user_agent)}
                      {s.current && <span data-testid="session-current-badge" className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary rounded px-1.5 py-0.5">Esta sesión</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono-num truncate">
                      IP {s.ip || "—"} · última actividad {fmtDate(s.last_seen)}
                    </div>
                  </div>
                  {!s.current && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-sm text-destructive hover:text-destructive"
                      data-testid={`revoke-session-${s.jti}`}
                      onClick={() => revoke(s.jti)}
                      disabled={busy === s.jti}
                    >
                      {busy === s.jti ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Cerrar"}
                    </Button>
                  )}
                </div>
              );
            })}
            {sessions.length === 0 && <p className="text-muted-foreground text-sm py-6">No hay sesiones activas.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
